/**
 * Damage numbers: a short-lived figure at the spot a hit landed, pinned to the
 * body it hit, for every hit the local fighter deals or takes. White for what
 * you deal, red with a minus for what you take; when a guard took the hit the
 * figure is dimmed and says what took it. Drawn as DOM over the canvas so the
 * figures stay crisp and use the HUD's type. Client-only: everything comes
 * from the server's hit events.
 */
import { MAX_TOTAL_HP, tuning, type Vec3 } from "@meleeroyale/sim";
import * as THREE from "three";
import { t } from "../i18n/index.ts";
import { type HitFlashEvent, HitFlashes, hitSeverity, type TargetPose } from "./hit-flash.ts";

/** A hit event as read off the wire, with what a number needs to explain itself. */
export interface PopHit extends HitFlashEvent {
  attacker: number;
  checked: boolean;
  collapsed: boolean;
  counter: boolean;
  /** "jammed" | "short" | "clean" | "overreach" */
  extension: string;
  /** Damage the kicker's own shin took from a check. */
  recoil: number;
}

/** Why a figure is what it is: a guard outcome, or the one factor that made it. */
export type PopNote =
  | ""
  | "blocked"
  | "parried"
  | "checked"
  | "guardBroke"
  | "counter"
  | "jammed"
  | "recoil"
  | "cage";

export interface DamagePop {
  /** Fighter the figure is pinned to. */
  target: number;
  /** Point in that fighter's local frame. */
  at: Vec3;
  /** Damage the local fighter dealt, or took. */
  kind: "dealt" | "taken";
  /** The figure as shown ("14", "−3.2"); empty when nothing got through. */
  text: string;
  note: PopNote;
  /** A guard took the hit: drawn smaller and dimmer. */
  guarded: boolean;
  /** 0 .. 1, sets the size. */
  severity: number;
}

/** One decimal below 10, where the decimal still says something; whole numbers above. */
export function formatDamage(damage: number): string {
  return damage < 10 ? damage.toFixed(1) : String(Math.round(damage));
}

function figure(damage: number, kind: DamagePop["kind"]): string {
  if (damage <= 0) return "";
  return kind === "taken" ? `−${formatDamage(damage)}` : formatDamage(damage);
}

/** The figures a hit shows the local fighter: none unless it dealt or took it. */
export function damagePops(hit: PopHit, myId: number): DamagePop[] {
  if (myId < 0 || (hit.attacker !== myId && hit.target !== myId)) return [];
  const kind = hit.target === myId ? "taken" : "dealt";
  const pops: DamagePop[] = [];
  if (hit.parried) {
    pops.push({ ...base(hit), kind, text: "", note: "parried", guarded: true, severity: 0 });
    return pops;
  }
  // A collapsed guard let the whole hit through: it is not dimmed.
  const guarded = hit.blocked && !hit.collapsed;
  const note: PopNote = hit.checked
    ? "checked"
    : hit.collapsed
      ? "guardBroke"
      : hit.blocked
        ? "blocked"
        : hit.counter
          ? "counter"
          : hit.extension === "jammed"
            ? "jammed"
            : "";
  pops.push({
    ...base(hit),
    kind,
    text: figure(hit.damage, kind),
    note,
    guarded,
    severity: guarded ? 0 : hitSeverity(hit.damage, hit.part),
  });
  // Shin on shin: the kicker's own shin pays, at the same spot.
  if (hit.recoil > 0) {
    const recoilKind = hit.attacker === myId ? "taken" : "dealt";
    pops.push({
      ...base(hit),
      kind: recoilKind,
      text: figure(hit.recoil, recoilKind),
      note: "recoil",
      guarded: false,
      severity: 0,
    });
  }
  return pops;
}

/** The cage crushing the local fighter, shown on the torso. */
export function crushPop(id: number, damage: number, myId: number): DamagePop | null {
  if (id !== myId || damage <= 0) return null;
  const b = tuning.body;
  return {
    target: id,
    at: { x: 0, y: (b.hipY + b.shoulderY) / 2, z: 0 },
    kind: "taken",
    text: figure(damage, "taken"),
    note: "cage",
    guarded: false,
    severity: 0,
  };
}

/** Health lost so far; nothing heals, so it is everything a fighter has taken. */
export function damageTaken(hp: readonly number[]): number {
  let left = 0;
  for (const h of hp) left += h;
  return Math.max(0, MAX_TOTAL_HP - left);
}

function base(hit: PopHit): Pick<DamagePop, "target" | "at"> {
  return { target: hit.target, at: { ...hit.at } };
}

/** Figures alive at once; the oldest is reused. */
const POOL = 12;
const LIFE_MS = 950;
const GUARDED_LIFE_MS = 700;
/** Screen pixels a figure rises over its life. */
const RISE_PX = 48;
/** Figures spawned back to back step sideways so a flurry does not stack into one. */
const NUDGE_PX = [0, 22, -22];
const POP_IN = 0.12;
const FADE_FROM = 0.55;

interface Live {
  el: HTMLDivElement;
  value: HTMLSpanElement;
  note: HTMLSpanElement;
  target: number;
  local: Vec3;
  startAt: number;
  life: number;
  nudge: number;
  active: boolean;
}

export class DamageNumbers {
  private readonly layer: HTMLElement;
  private readonly live: Live[] = [];
  private readonly point = new THREE.Vector3();
  private next = 0;
  private spawned = 0;

  constructor(layer: HTMLElement) {
    this.layer = layer;
    for (let i = 0; i < POOL; i += 1) {
      const el = document.createElement("div");
      el.className = "dmg";
      el.hidden = true;
      const value = document.createElement("span");
      value.className = "dmg-value num";
      const note = document.createElement("span");
      note.className = "dmg-note";
      el.append(value, note);
      layer.append(el);
      this.live.push({
        el,
        value,
        note,
        target: -1,
        local: { x: 0, y: 0, z: 0 },
        startAt: 0,
        life: LIFE_MS,
        nudge: 0,
        active: false,
      });
    }
  }

  /** Shows a figure `delayMs` from `now` (remote hits wait for the interpolated view). */
  spawn(pop: DamagePop, now: number, delayMs: number): void {
    const f = this.live[this.next];
    if (!f) return;
    this.next = (this.next + 1) % POOL;
    f.el.className = `dmg dmg-${pop.kind}${pop.guarded ? " dmg-guarded" : ""}`;
    f.el.style.setProperty("--dmg-scale", (1 + 0.6 * pop.severity).toFixed(2));
    f.value.textContent = pop.text;
    f.value.hidden = pop.text === "";
    f.note.textContent = pop.note ? t.match.pop[pop.note] : "";
    f.note.hidden = pop.note === "";
    f.target = pop.target;
    f.local = { ...pop.at };
    f.startAt = now + delayMs;
    f.life = pop.guarded ? GUARDED_LIFE_MS : LIFE_MS;
    f.nudge = NUDGE_PX[this.spawned % NUDGE_PX.length] ?? 0;
    this.spawned += 1;
    f.active = true;
  }

  /** Call after the frame is rendered, so the camera's matrices are this frame's. */
  update(now: number, camera: THREE.Camera, poseOf: TargetPose): void {
    const width = this.layer.clientWidth;
    const height = this.layer.clientHeight;
    for (const f of this.live) {
      if (!f.active) continue;
      const age = (now - f.startAt) / f.life;
      const pose = poseOf(f.target);
      if (age >= 1 || !pose) {
        f.active = false;
        f.el.hidden = true;
        continue;
      }
      const p = HitFlashes.world(pose, f.local);
      this.point.set(p.x, p.y, p.z).project(camera);
      // Not yet due, or behind the camera.
      if (age < 0 || this.point.z > 1) {
        f.el.hidden = true;
        continue;
      }
      const x = (this.point.x * 0.5 + 0.5) * width + f.nudge;
      const rise = 1 - (1 - age) * (1 - age);
      const y = (-this.point.y * 0.5 + 0.5) * height - RISE_PX * rise;
      const scale = age < POP_IN ? 1.4 - 0.4 * (age / POP_IN) : 1;
      const opacity = age < FADE_FROM ? 1 : 1 - (age - FADE_FROM) / (1 - FADE_FROM);
      f.el.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) translate(-50%, -100%) scale(${scale.toFixed(3)})`;
      f.el.style.opacity = opacity.toFixed(3);
      f.el.hidden = false;
    }
  }

  dispose(): void {
    for (const f of this.live) f.el.remove();
  }
}
